import type { NativeWidgetIntent } from '../services/nativeWidgetBridge'
import type { Workspace } from '../types/workspace'
import { featureFlag } from '../utils/featureFlags'
import { isPersonalWorkspace, isTeamWorkspace, resolvePersonalForUser } from '../utils/workspaceKind'
import { openWorkspaceTarget } from '../services/workspaceSwitch/openWorkspaceTarget'

export type MemberTabKey =
  | 'for-you'
  | 'board'
  | 'ai'
  | 'nexus'
  | 'calendar'
  | 'chat'
  | 'controls'
  | 'you'
  | 'forms'
  | 'sync'
  | 'automations'
  | 'team'
  | 'stock'
  | 'class'
  | 'bookings'
  | 'ops'
export type MemberAiSubTab = 'HISTORY' | 'CREATE' | 'EXECUTE' | 'PULSE' | 'MAGIC'
export type MemberCollaborationSubTab = 'CHAT' | 'CALENDAR' | 'HUDDLE' | 'MEETINGS'
export type MemberWidgetPanel = 'BROADCAST' | 'VOUCH' | 'VOICE' | null

/** Reserved URL segment for the current user's personal workspace. */
export const PERSONAL_SLUG_SEGMENT = 'personal'

const personalInMemberAppEnabled = (): boolean => featureFlag('personalInMemberApp')

export const MEMBER_AI_SUBTABS: readonly MemberAiSubTab[] = ['HISTORY', 'CREATE', 'EXECUTE', 'PULSE', 'MAGIC']

export const isMemberAiSubTab = (value: unknown): value is MemberAiSubTab =>
  typeof value === 'string' && (MEMBER_AI_SUBTABS as readonly string[]).includes(value)

export type MemberRoute = {
  slug: string
  tab: MemberTabKey
  collaborationSub?: MemberCollaborationSubTab
  aiSub?: MemberAiSubTab
}

export type MemberNativeWidgetIntentResolution =
  | { kind: 'NAVIGATE'; path: string; aiSub?: MemberAiSubTab }
  | { kind: 'OPEN_WIDGET_PANEL'; panel: Exclude<MemberWidgetPanel, null> }
  | { kind: 'NONE' }

export const isSyntheticChecklistParent = (parentId?: string | null): boolean =>
  parentId === 'workspace-default-checklist' || Boolean(parentId?.startsWith('board-root:'))

const normalizeTab = (tab: string | undefined): MemberTabKey =>
  tab === 'board' ||
  tab === 'ai' ||
  tab === 'nexus' ||
  tab === 'calendar' ||
  tab === 'chat' ||
  tab === 'controls' ||
  tab === 'you' ||
  tab === 'forms' ||
  tab === 'sync' ||
  tab === 'automations' ||
  tab === 'team' ||
  tab === 'stock' ||
  tab === 'class' ||
  tab === 'bookings' ||
  tab === 'ops'
    ? tab
    : 'for-you'

export const parseMemberRoute = (pathname: string): MemberRoute | null => {
  const parts = pathname.split('/').filter(Boolean)
  if (parts[0] !== 'member' || !parts[1]) return null
  if (parts[2] === 'chat') return { slug: parts[1], tab: 'nexus', collaborationSub: 'CHAT' }
  if (parts[2] === 'calendar') return { slug: parts[1], tab: 'nexus', collaborationSub: 'CALENDAR' }
  if (parts[2] === 'huddle') return { slug: parts[1], tab: 'nexus', collaborationSub: 'HUDDLE' }
  if (parts[2] === 'meetings') return { slug: parts[1], tab: 'nexus', collaborationSub: 'MEETINGS' }
  if (parts[2] === 'controls') {
    const section = parts[3]?.toLowerCase()
    if (section === 'ai') return { slug: parts[1], tab: 'ai', aiSub: 'MAGIC' }
    if (section === 'members' || section === 'team') return { slug: parts[1], tab: 'team' }
    if (section === 'forms') return { slug: parts[1], tab: 'forms' }
    if (section === 'sync') return { slug: parts[1], tab: 'sync' }
    if (section === 'automations') return { slug: parts[1], tab: 'automations' }
    if (section === 'stock') return { slug: parts[1], tab: 'stock' }
    return { slug: parts[1], tab: 'for-you' }
  }
  return { slug: parts[1], tab: normalizeTab(parts[2]) }
}

export type BuildMemberPathArgs = {
  workspace: Pick<Workspace, 'slug' | 'isPersonal' | 'kind'> | string
  tab?: MemberTabKey | string
  subTab?: string
  query?: Record<string, string | number | boolean | null | undefined> | string
}

export const buildMemberBasePath = (
  workspace: Pick<Workspace, 'slug' | 'isPersonal' | 'kind'> | string,
): string => {
  const slugSegment = typeof workspace === 'string'
    ? (workspace === PERSONAL_SLUG_SEGMENT ? PERSONAL_SLUG_SEGMENT : workspace)
    : (isPersonalWorkspace(workspace) ? PERSONAL_SLUG_SEGMENT : (workspace.slug || PERSONAL_SLUG_SEGMENT))
  return `/member/${slugSegment}`
}

export const buildMemberPath = ({ workspace, tab = 'for-you', subTab, query }: BuildMemberPathArgs): string => {
  const base = `${buildMemberBasePath(workspace)}/${tab}`
  const withSub = subTab ? `${base}/${subTab}` : base

  if (!query) return withSub

  if (typeof query === 'string') {
    const cleanQuery = query.trim()
    if (!cleanQuery) return withSub
    return `${withSub}${cleanQuery.startsWith('?') ? cleanQuery : `?${cleanQuery}`}`
  }

  const searchParams = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null) {
      searchParams.set(k, String(v))
    }
  }
  const serialized = searchParams.toString()
  return serialized ? `${withSub}?${serialized}` : withSub
}

type ParseMemberPathArgs = {
  uid: string | null | undefined
  workspaces: readonly Pick<Workspace, 'id' | 'slug' | 'isPersonal' | 'kind' | 'ownerId'>[]
}

export type ParsedMemberPath = {
  workspaceId: string
  slugSegment: string
  isPersonalRoute: boolean
  tab: MemberTabKey | null
  subTab: string | null
}

export const parseMemberPath = (
  url: string,
  { uid, workspaces }: ParseMemberPathArgs,
): ParsedMemberPath | null => {
  let slugSegment: string
  let tabRaw: string | undefined
  let subTabRaw: string | undefined

  if (url.startsWith('/personal')) {
    slugSegment = PERSONAL_SLUG_SEGMENT
    const cleanUrl = url.replace(/^\/personal\/?/, '').split(/[?#]/)[0]
    const parts = cleanUrl ? cleanUrl.split('/').filter(Boolean) : []
    tabRaw = parts[0]
    subTabRaw = parts[1]
  } else {
    const match = url.match(/^\/member\/([^/?#]+)(?:\/([^/?#]+))?(?:\/([^/?#]+))?/)
    if (!match) return null
    ;[, slugSegment, tabRaw, subTabRaw] = match
  }

  const tab = tabRaw ? normalizeTab(tabRaw) : null
  const subTab = subTabRaw ?? null

  const personal = resolvePersonalForUser(uid, workspaces)
  const isPersonalMatch =
    slugSegment === PERSONAL_SLUG_SEGMENT ||
    Boolean(personal && (personal.slug === slugSegment || personal.id === slugSegment))

  if (isPersonalMatch) {
    if (!personalInMemberAppEnabled()) return null
    if (!personal) return null
    return { workspaceId: personal.id, slugSegment, isPersonalRoute: true, tab: tab ?? 'for-you', subTab }
  }

  const team = workspaces.find((workspace) => isTeamWorkspace(workspace) && workspace.slug === slugSegment)
  if (!team) return null
  return { workspaceId: team.id, slugSegment, isPersonalRoute: false, tab, subTab }
}

export const buildMemberChecklistItemPath = (
  slug: string,
  checklistId: string,
  parentElementId?: string | null,
  pendingStatus?: string | null,
  targetSection?: string | null,
): string => {
  const parentQuery = parentElementId && !isSyntheticChecklistParent(parentElementId)
    ? `node=${encodeURIComponent(parentElementId)}&`
    : ''
  const pendingQuery = pendingStatus ? `&pendingStatus=${encodeURIComponent(pendingStatus)}` : ''
  const sectionQuery = targetSection ? `&section=${encodeURIComponent(targetSection)}` : ''
  return buildMemberPath({
    workspace: slug,
    tab: 'board',
    query: `${parentQuery}checklistItem=${encodeURIComponent(checklistId)}${pendingQuery}${sectionQuery}`,
  })
}

export const buildMemberBoardNodePath = (
  slug: string,
  elementId: string,
  openChecklist = false,
): string =>
  buildMemberPath({
    workspace: slug,
    tab: 'board',
    query: `node=${encodeURIComponent(elementId)}${openChecklist ? '&checklist=1' : ''}`,
  })

export const resolveMemberNativeWidgetIntent = (
  intent: NativeWidgetIntent,
  slug: string,
  options?: {
    activeWorkspaceId?: string | null
    openWorkspaceTarget?: typeof openWorkspaceTarget
  },
): MemberNativeWidgetIntentResolution => {
  if (intent.workspaceId && options?.activeWorkspaceId && intent.workspaceId !== options.activeWorkspaceId) {
    const fn = options.openWorkspaceTarget ?? openWorkspaceTarget
    void fn({
      workspaceId: intent.workspaceId,
      source: 'WIDGET',
    })
  }
  if (intent.action === 'member_pulse') {
    return { kind: 'NAVIGATE', path: buildMemberPath({ workspace: slug, tab: 'ai' }), aiSub: 'PULSE' }
  }
  if (intent.action === 'member_controls') {
    return { kind: 'NAVIGATE', path: buildMemberPath({ workspace: slug, tab: 'automations' }) }
  }
  if (intent.action === 'member_broadcast') return { kind: 'OPEN_WIDGET_PANEL', panel: 'BROADCAST' }
  if (intent.action === 'member_vouch') return { kind: 'OPEN_WIDGET_PANEL', panel: 'VOUCH' }
  if (intent.action === 'member_voice') return { kind: 'OPEN_WIDGET_PANEL', panel: 'VOICE' }
  return { kind: 'NONE' }
}
